import React, { FC } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_CURRENCIES,
  LIBRARY_TECHNOLOGIES_QUERY,
  TECHNOLOGY_UNITS_QUERY,
} from "../../../graphql/query";
import {
  UPDATE_LIBRARY_TECHNOLOGIES,
  UPDATE_TECHNOLOGY_UNITS,
  CREATE_LIBRARY_TECHNOLOGY,
  CREATE_TECHNOLOGY_UNIT,
  DELETE_TECHNOLOGY_UNIT,
  UPDATE_MANY_TECHNOLOGY_RATES,
  CREATE_MANY_TECHNOLOGY_RATES,
} from "../../../graphql/mutation";
import {
  UpdateLibraryTechnologiesMutation,
  UpdateLibraryTechnologiesMutationVariables,
  UpdateTechnologyUnitsMutation,
  UpdateTechnologyUnitsMutationVariables,
  CreateLibraryTechnologyMutation,
  CreateLibraryTechnologyMutationVariables,
  CreateTechnologyUnitMutation,
  CreateTechnologyUnitMutationVariables,
  DeleteTechnologyUnitMutation,
  DeleteTechnologyUnitMutationVariables,
  FindLibraryTechnologiesQuery,
  FindLibraryTechnologiesQueryVariables,
  FindTechnologyUnitsQuery,
  FindTechnologyUnitsQueryVariables,
  UpdateManyTechnologyRatesMutation,
  UpdateManyTechnologyRatesMutationVariables,
  CreateManyTechnologyRatesMutation,
  CreateManyTechnologyRatesMutationVariables,
  TechnologyUnit,
  LibraryTechnology,
} from "~/gql/graphql";
import { TechnologyDataImport } from "./technology-data-import";
import { useSettings } from "~/hooks";

export const TechnologyDataImportWrapper: FC = () => {
  const { settings } = useSettings();
  const currencySymbol = settings?.currency.symbol;
  const enableTechPricing = settings?.enableTechPricing ?? false;

  const { loading: libraryTechnologiesLoading, data: libraryTechnologiesData } = useQuery(
    LIBRARY_TECHNOLOGIES_QUERY
  );
  const { loading: technologyUnitsLoading, data: technologyUnitsData } =
    useQuery(TECHNOLOGY_UNITS_QUERY);

  const { data: currencies } = useQuery(GET_CURRENCIES);

  const [updateLibraryTechnologies, { loading: updateLibraryTechnologiesLoading }] = useMutation<
    UpdateLibraryTechnologiesMutation,
    UpdateLibraryTechnologiesMutationVariables
  >(UPDATE_LIBRARY_TECHNOLOGIES, {
    refetchQueries: [LIBRARY_TECHNOLOGIES_QUERY],
  });

  const [updateTechnologyUnits, { loading: updateTechnologyUnitsLoading }] = useMutation<
    UpdateTechnologyUnitsMutation,
    UpdateTechnologyUnitsMutationVariables
  >(UPDATE_TECHNOLOGY_UNITS, {
    refetchQueries: [TECHNOLOGY_UNITS_QUERY],
  });

  const [createLibraryTechnology] = useMutation<
    CreateLibraryTechnologyMutation,
    CreateLibraryTechnologyMutationVariables
  >(CREATE_LIBRARY_TECHNOLOGY, {
    update(cache, { data }) {
      if (!data?.createLibraryTechnology) return;
      const existingData = cache.readQuery<
        FindLibraryTechnologiesQuery,
        FindLibraryTechnologiesQueryVariables
      >({
        query: LIBRARY_TECHNOLOGIES_QUERY,
      });
      cache.writeQuery<FindLibraryTechnologiesQuery, FindLibraryTechnologiesQueryVariables>({
        query: LIBRARY_TECHNOLOGIES_QUERY,
        data: {
          libraryTechnologies: [
            ...(existingData?.libraryTechnologies || []),
            data.createLibraryTechnology,
          ],
        },
      });
    },
  });

  const [createTechnologyUnit] = useMutation<
    CreateTechnologyUnitMutation,
    CreateTechnologyUnitMutationVariables
  >(CREATE_TECHNOLOGY_UNIT, {
    update(cache, { data }) {
      if (!data?.createTechnologyUnit) return;
      const existingData = cache.readQuery<
        FindTechnologyUnitsQuery,
        FindTechnologyUnitsQueryVariables
      >({
        query: TECHNOLOGY_UNITS_QUERY,
      });
      cache.writeQuery<FindTechnologyUnitsQuery, FindTechnologyUnitsQueryVariables>({
        query: TECHNOLOGY_UNITS_QUERY,
        data: {
          technologyUnits: [...(existingData?.technologyUnits || []), data.createTechnologyUnit],
        },
      });
    },
  });

  const [deleteTechnologyUnit] = useMutation<
    DeleteTechnologyUnitMutation,
    DeleteTechnologyUnitMutationVariables
  >(DELETE_TECHNOLOGY_UNIT, {
    refetchQueries: [TECHNOLOGY_UNITS_QUERY],
  });

  const [updateManyTechnologyRates, { loading: updateManyTechnologyRatesLoading }] = useMutation<
    UpdateManyTechnologyRatesMutation,
    UpdateManyTechnologyRatesMutationVariables
  >(UPDATE_MANY_TECHNOLOGY_RATES, {
    refetchQueries: [LIBRARY_TECHNOLOGIES_QUERY],
  });

  const [createManyTechnologyRates, { loading: createManyTechnologyRatesLoading }] = useMutation<
    CreateManyTechnologyRatesMutation,
    CreateManyTechnologyRatesMutationVariables
  >(CREATE_MANY_TECHNOLOGY_RATES as any, {
    refetchQueries: [LIBRARY_TECHNOLOGIES_QUERY],
  });

  return (
    <TechnologyDataImport
      libraryTechnologies={
        (libraryTechnologiesData?.libraryTechnologies || []) as LibraryTechnology[]
      }
      technologyUnits={(technologyUnitsData?.technologyUnits || []) as TechnologyUnit[]}
      createLibraryTechnology={createLibraryTechnology}
      createTechnologyUnit={createTechnologyUnit}
      deleteTechnologyUnit={deleteTechnologyUnit}
      updateLibraryTechnology={updateLibraryTechnologies}
      updateTechnologyUnit={updateTechnologyUnits}
      updateManyTechnologyRates={updateManyTechnologyRates}
      createManyTechnologyRates={createManyTechnologyRates}
      updateLoading={
        updateLibraryTechnologiesLoading ||
        updateTechnologyUnitsLoading ||
        updateManyTechnologyRatesLoading ||
        createManyTechnologyRatesLoading
      }
      queryLoading={libraryTechnologiesLoading || technologyUnitsLoading}
      currencySymbol={currencySymbol || ""}
      enableTechPricing={enableTechPricing}
      currencies={currencies?.currencies || []}
    />
  );
};
